import { useState, useEffect, forwardRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Cookies from "universal-cookie";
import Select from "react-select";
import { Title, LoadingTitle } from "../../components/Title.jsx";
import "../../stylesheets/Billing.scss";
import { BillingTable } from "../../components/table/billing/BillingTable";
import * as FaIcons from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getFileUrl, getFileName } from "../../utils/fileUrl";

export const Billing = () => {
  const cookies = new Cookies();
  const token = cookies.get("token_user");
  const navigate = useNavigate();

  if (!token) {
    navigate("/login");
  }

  const URL = process.env.REACT_APP_API_URL + "/user";

  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(null);

  // Date range (frontend filter told by you)
  const [invoiceStartDate, setInvoiceStartDate] = useState(null);
  const [invoiceEndDate, setInvoiceEndDate] = useState(null);
  // Multi-level calendar view state
  const [calendarView, setCalendarView] = useState('day');
  const [calendarOpen, setCalendarOpen] = useState(false);

  const clearInvoiceDateRange = () => {
    setInvoiceStartDate(null);
    setInvoiceEndDate(null);
  };

  const getInvoices = async () => {
    try {
      const response = await fetch(`${URL}/invoices`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      const data = await response.json();
      if (data.result) setInvoices(data.result);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    getInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAccountChange = (selectedOption) => {
    setSelectedAccount(selectedOption ? selectedOption.value : null);
  };

  const accountOptions = invoices?.length
    ? [...new Set(invoices.map((invoice) => invoice.account_no))]
      .filter(Boolean)
      .map((account) => ({ value: account, label: account }))
      .sort((a, b) => a.label.localeCompare(b.label))
    : [];

  const paymentStatusOptions = [
    { value: "1", label: "Paid" },
    { value: "0", label: "Unpaid" },
  ];

  const handlePaymentStatusChange = (selectedOption) => {
    setSelectedPaymentStatus(selectedOption ? selectedOption.value : null);
  };

  // created_at comes as "MM/DD/YYYY" (ex: "03/19/2025") -> YYYYMMDD
  const dateKeyFromCreatedAt = (raw) => {
    if (!raw) return null;

    const s = String(raw).trim();

    // MM/DD/YYYY
    const mdy = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (mdy) {
      const mm = Number(mdy[1]);
      const dd = Number(mdy[2]);
      const yyyy = Number(mdy[3]);
      if (!yyyy || mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
      return yyyy * 10000 + mm * 100 + dd;
    }

    // fallback: allow "YYYY-MM-DD ..." if it ever shows up
    const ymd = s.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) {
      const yyyy = Number(ymd[1]);
      const mm = Number(ymd[2]);
      const dd = Number(ymd[3]);
      return yyyy * 10000 + mm * 100 + dd;
    }

    return null;
  };
  const dateKeyFromJs = (d) =>
    d ? d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate() : null;

  const filteredInvoices = invoices.filter((invoice) => {
    const search = searchTerm.toLowerCase();

    const rangeComplete = !!invoiceStartDate && !!invoiceEndDate;
    const cellKey = dateKeyFromCreatedAt(invoice?.created_at);

    const matchesDate =
      !rangeComplete ||
      (cellKey &&
        cellKey >= dateKeyFromJs(invoiceStartDate) &&
        cellKey <= dateKeyFromJs(invoiceEndDate));

    const matchesSearch =
      (invoice?.file_number || "").toLowerCase().includes(search) ||
      (invoice?.customer_reference_no || "").toLowerCase().includes(search) ||
      (invoice?.proper_name || "").toLowerCase().includes(search) ||
      (invoice?.account_no || "").toLowerCase().includes(search) ||
      (invoice?.master_bill_lading || "").toLowerCase().includes(search) ||
      String(invoice?.is_paid ?? "").toLowerCase().includes(search) ||
      (invoice?.containers || []).some((c) =>
        String(c || "").toLowerCase().includes(search)
      ) ||
      (invoice?.created_at || "").toLowerCase().includes(search);

    const matchesAccount = !selectedAccount || invoice.account_no === selectedAccount;

    const matchesPaymentStatus =
      selectedPaymentStatus === null ||
      String(invoice.is_paid) === String(selectedPaymentStatus);

    return matchesSearch && matchesAccount && matchesPaymentStatus && matchesDate;
  });

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "38px",
      borderRadius: "5px",
      border: "1px solid #ccc",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
    singleValue: (provided) => ({
      ...provided,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
    placeholder: (provided) => ({
      ...provided,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
  };


  const CalendarIconButton = forwardRef(({ onClick, title }, ref) => (
    <button
      type="button"
      className="date-filter-icon-btn"
      onClick={(e) => {
        e.preventDefault();
        onClick?.(e);
      }}
      ref={ref}
      title={title || "Date Filter"}
      aria-label={title || "Date Filter"}
    >
      <FaIcons.FaCalendarAlt />
    </button>
  ));


  const DateFilterCalendarContainer = ({ className, children, clear, canClear }) => {
    return (
      <div className={className}>
        <div className="date-filter-popover-header">
          {canClear ? (
            <button
              type="button"
              className="date-filter-popover-clear-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clear();
              }}
              aria-label="Clear date filter"
              title="Clear"
            >
              ×
            </button>
          ) : null}
        </div>
        {children}
      </div>
    );
  };

  // Multi-level calendar header
  const renderCustomHeader = ({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => {
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ];
    if (calendarView === 'day') {
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 8px' }}>
          <button type="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>{'<'}</button>
          <span style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => setCalendarView('month')}>
            {monthNames[date.getMonth()]} {date.getFullYear()}
          </span>
          <button type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>{'>'}</button>
        </div>
      );
    }
    if (calendarView === 'month') {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '0 8px' }}>
          <span style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => setCalendarView('year')}>
            {date.getFullYear()}
          </span>
        </div>
      );
    }
    if (calendarView === 'year') {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '0 8px' }}>
          <span style={{ fontWeight: 'bold' }}>Select Year</span>
        </div>
      );
    }
    return null;
  };

  // DatePicker props for multi-level view
  let datePickerProps = {
    selectsRange: true,
    startDate: invoiceStartDate,
    endDate: invoiceEndDate,
    onChange: (dates) => {
      const [s, e] = dates;
      setInvoiceStartDate(s);
      setInvoiceEndDate(e);
      if (s && e) {
        setCalendarOpen(false); // Only close after both dates selected in day view
      }
    },
    isClearable: false,
    dateFormat: "MMMM dd",
    placeholderText: "Date Filter",
    customInput: <CalendarIconButton title="Date Filter" />,
    popperPlacement: "bottom-end",
    calendarContainer: (props) => (
      <DateFilterCalendarContainer
        {...props}
        clear={clearInvoiceDateRange}
        canClear={!!invoiceStartDate || !!invoiceEndDate}
      />
    ),
    renderCustomHeader,
    open: calendarOpen,
    onClickOutside: () => setCalendarOpen(false),
    onInputClick: () => setCalendarOpen(true),
    onSelect: undefined,
  };

  if (calendarView === 'month') {
    datePickerProps.showMonthYearPicker = true;
    datePickerProps.dateFormat = "MMMM yyyy";
    datePickerProps.onChange = (date) => {
      // Only switch view, do not set date
      setCalendarView('day');
    };
    datePickerProps.selectsRange = false;
    datePickerProps.selected = invoiceStartDate || new Date();
    datePickerProps.onSelect = undefined;
  } else if (calendarView === 'year') {
    datePickerProps.showYearPicker = true;
    datePickerProps.dateFormat = "yyyy";
    datePickerProps.onChange = (date) => {
      setCalendarView('month');
    };
    datePickerProps.selectsRange = false;
    datePickerProps.selected = invoiceStartDate || new Date();
    datePickerProps.onSelect = undefined;
  }

  return (
    <>
      <Title
        title="Billing"
        links={[
          { link: "/", title: "Home" },
          { link: "/billing", title: "Billing" },
        ]}
      />

      <div id="billing" className="page-content">
        <h3>Payment Methods</h3>

        <div className="payment-boxes">
          <div className="payment-box" id="box-1">
            <p className="payment-header">ACH/Wire</p>
            <div className="payment-body">
              <p className="box-2_first-body">
                Account: 472011571 <br />
                Routing: 021000021
              </p>
              <div className="box-2_second-body">
                <p>
                  Swift Code: CHASUS33 <br />
                  Bank: Chase
                </p>
              </div>
            </div>
          </div>

          <div className="payment-box" id="box-2">
            <p className="payment-header">Quickpay/Zelle</p>
            <div className="payment-body">
              <p>
                username is <br />
                hillel@atlanticfb.com
              </p>
            </div>
          </div>

          <div className="payment-box" id="box-3">
            <p className="payment-header">Email Check Image</p>
            <div className="payment-body">
              <p id="right">
                Send to:
                <br />
                <a href="mailto:hillel@atlanticfb.com" className="email">
                  hillel@atlanticfb.com
                </a>
                <br />
                <a href="mailto:mimi@atlanticfb.com" className="email">
                  mimi@atlanticfb.com
                </a>
                <br />
                <a href="mailto:abraham@atlanticfb.com" className="email">
                  abraham@atlanticfb.com
                </a>
              </p>
              <a
                className="email"
                href="mailto:abraham@atlanticfb.com; mailto:mimi@atlanticfb.com; mailto:abraham@atlanticfb.com"
              >
                Send to all
              </a>
            </div>
          </div>

          <div className="payment-box" id="box-4">
            <p className="payment-header">Mail Check</p>
            <div className="payment-body">
              <p>
                Atlantic Freight Brokers <br />
                453 Broadway STE 111. Brooklyn NY 11211
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="billing-filters">
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search"
            style={{
              flex: 1,
              minWidth: "280px",
              padding: "10px",
              border: "1px solid #ccc",
              borderRadius: "5px",
            }}
          />

          <Select
            styles={customSelectStyles}
            options={accountOptions}
            className="custom-select"
            placeholder="Account No"
            onChange={handleAccountChange}
            isClearable
          />

          <Select
            styles={customSelectStyles}
            options={paymentStatusOptions}
            className="custom-select"
            placeholder="Payment Status"
            onChange={handlePaymentStatusChange}
            isClearable
          />

          {/* Calendar icon button filter */}
          <div className="date-filter">
            <DatePicker {...datePickerProps} />
          </div>
        </div>

        <div className="table">
          {filteredInvoices && <BillingTable invoices={filteredInvoices} />}
        </div>
      </div>
    </>
  );
};

export const BillingInvoice = () => {
  const { id } = useParams();

  const cookies = new Cookies();
  const token = cookies.get("token_user");
  const navigate = useNavigate();

  if (!token) {
    navigate("/login");
  }

  const URL = process.env.REACT_APP_API_URL + "/user";
  const [invoice, setInvoice] = useState();

  const getInvoices = async () => {
    try {
      const response = await fetch(`${URL}/invoices/${id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      let data = await response.json();
      if (data?.invoice) setInvoice(data?.invoice);
    } catch (e) {
      // ignore
    }
  };

  const getUrl = (path) => getFileUrl(path);

  useEffect(() => {
    getInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {invoice ? (
        <Title title={`Invoice ${invoice?.shipment?.file_number}`} />
      ) : (
        <LoadingTitle />
      )}

      <div id="invoice" className="page-content">
        <div className="left-side">
          {invoice && (
            <embed type="application/pdf" src={getUrl(invoice?.path)} width="100%" height="100%" />
          )}
        </div>

        <div className="right-side">
          <section id="section-2" className="pay-info">
            <p className="title">ACH/Wire</p>
            <div className="pay-links">
              <p id="right">
                Account: 472011571 <br />
                Routing: 021000021
              </p>
              <p id="left">
                Swift Code: CHASUS33 <br />
                Bank: Chase
              </p>
            </div>
          </section>

          <section id="section-3" className="pay-info">
            <p className="title">Quickpay/Zelle</p>
            <div className="pay-links">
              <p id="right">
                Recipient: <span className="email">hillel@atlanticfb.com</span>
              </p>
            </div>
          </section>

          <section id="section-4" className="pay-info">
            <p className="title">Email Check Image</p>
            <div className="pay-links">
              <p id="right">
                Send to:
                <br />
                <a href="mailto:hillel@atlanticfb.com" className="email">
                  hillel@atlanticfb.com
                </a>
                <br />
                <a href="mailto:mimi@atlanticfb.com" className="email">
                  mimi@atlanticfb.com
                </a>
                <br />
                <a href="mailto:abraham@atlanticfb.com" className="email">
                  abraham@atlanticfb.com
                </a>
              </p>
              <a
                className="email"
                href="mailto:abraham@atlanticfb.com; mailto:mimi@atlanticfb.com; mailto:abraham@atlanticfb.com"
              >
                Send to all
              </a>
            </div>
          </section>

          <section id="section-5" className="pay-info">
            <p className="title">Mail Check</p>
            <div className="pay-links">
              <p id="right">
                Atlantic Freight Brokers <br />
                453 Broadway STE 111. Brooklyn NY 11211
              </p>
            </div>
          </section>

          {invoice && invoice.backup?.length > 0 && (
            <a
              className="name"
              href={getFileUrl(invoice.backup)}
              target="_blank"
              rel="noreferrer"
            >
              <div className="file-visualizer flex jc-c ai-c column available">
                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">
                  <path
                    id="file-arrow-down-solid"
                    d="M30,10H20V0ZM20,12.5H30V36.25A3.75,3.75,0,0,1,26.25,40H3.75A3.75,3.75,0,0,1,0,36.25V3.75A3.75,3.75,0,0,1,3.75,0H17.5V10A2.5,2.5,0,0,0,20,12.5Zm-.078,10.547L16.875,26.1V18.125a1.875,1.875,0,0,0-3.75,0V26.1l-3.117-3.055A1.6,1.6,0,0,0,8.75,22.5a1.855,1.855,0,0,0-1.326.547,1.875,1.875,0,0,0,0,2.652l6.25,6.25a1.875,1.875,0,0,0,2.652,0l6.25-6.25a1.876,1.876,0,1,0-2.654-2.652Z"
                    fill="#23b574"
                  />
                </svg>

                {getFileName(invoice?.backup)}
              </div>
            </a>
          )}
        </div>
      </div>
    </>
  );
};
